import {RootState} from "../../../store";
import {ICurrentMachinery, IGeoLocation} from "../../../models/iMachinery";
import {ITask} from "../../../models/IMachineryTasks";
import {createSelector} from "@reduxjs/toolkit";
import {selectLastMaintenanceHistory} from "../../maintenance/model/selectors";
import {selectMachineryGroupFilter} from "../../machinery_groups/model/selectors";
import {NO_GROUP_FILTER} from "../../../models/iMachineryGroup";

const selectMachineryState = (state: RootState) => state.machinery;

export const selectAllMachinery = createSelector(
    [selectMachineryState],
    (machineryState) => [...machineryState.list].sort((a, b) => {
        const brandCmp = (a.brand ?? "").localeCompare(b.brand ?? "", "ru");
        if (brandCmp !== 0) return brandCmp;
        return (a.model ?? "").localeCompare(b.model ?? "", "ru");
    })
);
export const selectMachineryIsLoading = createSelector([selectMachineryState], (machineryState) => machineryState.isLoading);

export const selectMachineryTypeFilter = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.typeFilter,
);

/**
 * Список машин после применения фильтра по типу — поверх отсортированного
 * selectAllMachinery (сортировку brand/model не дублируем).
 */
export const selectFilteredMachinery = createSelector(
    [selectAllMachinery, selectMachineryTypeFilter],
    (list, typeFilter) =>
        typeFilter == null ? list : list.filter((m) => m.type_id === typeFilter),
);

export const selectAllMachineryForOptions = createSelector([selectMachineryState], (machineryState) =>
    machineryState.list.map((machinery) => ({
        id: machinery.id,
        title: `${machinery.brand} ${machinery.model} ${machinery.state_number}`,
    })),
);

export const selectMachineryOptions = createSelector(
    [selectMachineryState],
    (machineryState) => [...machineryState.options]
        .sort((a, b) => {
            const brandCmp = (a.brand ?? "").localeCompare(b.brand ?? "", "ru");
            if (brandCmp !== 0) return brandCmp;
            return (a.model ?? "").localeCompare(b.model ?? "", "ru");
        })
        .map((m) => ({
            id: m.id,
            title: `${m.brand} ${m.model} ${m.state_number}`,
            // Тип нужен телеметрии (скрытие скорости/пробега у стационарной).
            // Селект машин в шапке читает только id и title — его не касается.
            type_id: m.type_id,
        })),
);

export const selectCurrentMachinery = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.current,
);

/**
 * Пороги по умолчанию для текущей машины. null — ещё не загружены или
 * запрос не удался; карточка тогда рисует прочерки.
 */
export const selectThresholdDefaults = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.thresholdDefaults,
);

export const selectMachineryOutputs = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.outputs,
);

export const selectMachineryInputs = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.inputs,
);

export const selectInputPresets = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.inputPresets,
);

export const selectCurrentMachineryId = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.current?.id,
);

export const selectCurrentMachineryPhotos = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.current?.photos,
);

export const selectCurrentMachineryTitle = createSelector([selectMachineryState], (machineryState) =>
    machineryState.current ? `${machineryState.current.brand} ${machineryState.current.model} ${machineryState.current.state_number}` : "",
);

export const getMachineryIsLoading = (state: RootState): boolean => {
    return state.machinery.isLoading;
};

export const getCurrentMachinery = (state: RootState): ICurrentMachinery | null => {
    return state.machinery.current;
};

export const getCurrentMachineryTitle = (state: RootState): string => {
    const currentMachinery = getCurrentMachinery(state);
    if (currentMachinery) {
        return `${currentMachinery.brand} ${currentMachinery.model} ${currentMachinery.state_number}`;
    } else {
        return "";
    }
};

export const getMeasuredValuesCurrentMachinery = (state: RootState): IGeoLocation | null => {
    return state.machinery.current?.current_location || null;
};

export const getCurrentMachineryOperatingTypeId = (state: RootState): number | null => {
    return state.machinery.current?.operating_type_id || null;
};

export const getTaskById = (state: RootState, taskId: string): ITask | null => {
    return state.machinery.current?.tasks.find((task) => task.id === taskId) || null;
};

/**
 * ============================================================================
 * Прогноз "до следующего ТО".
 * ============================================================================
 *
 * Считается на каждую ось независимо (км / моточасы / дни) по формуле:
 *
 *   остаток = (база_на_момент_ТО + интервал) − текущее
 *
 * где:
 *   - база_на_момент_ТО — result_odometer / result_operating / result_date
 *     последнего ЗАВЕРШЁННОГО ТО (status_id === 3);
 *   - интервал — maintenance_interval_km / _hours / _months из машины;
 *   - текущее — machinery.odometer / operating (учётные, обновляются ~раз в час),
 *     для дней — текущая дата.
 *
 * Ось включается в результат ТОЛЬКО если заданы все три слагаемых
 * (база, интервал, текущее). Если по какой-то оси чего-то нет — ось
 * не показывается (на легковой обычно км+месяцы, на спецтехнике — часы).
 *
 * remaining < 0 → ТО просрочено (overdue). Это важный сигнал, не прячем.
 *
 * Дни: месяцы → дни приблизительно (30 дней/мес).
 * ============================================================================
 */

export interface MaintenanceAxisForecast {
    /** Остаток до следующего ТО по оси (может быть отрицательным — просрочка). */
    remaining: number;
    /** true, если remaining < 0. */
    overdue: boolean;
    /** true, если ТО близко: 0 <= remaining < 10% интервала (и не overdue). */
    soon: boolean;
    /**
     * Остаток в долях интервала (remaining / interval). Общая мера для осей
     * с разными единицами: по ней выбирается ближайшая ось.
     */
    ratio: number;
}

export interface NextMaintenanceForecast {
    km?: MaintenanceAxisForecast;
    hours?: MaintenanceAxisForecast;
    days?: MaintenanceAxisForecast;
    /** true, если есть хотя бы одна ось для показа. */
    hasAny: boolean;
}

const DAY_MS = 86400000;

/** Доля интервала, ниже которой ТО считается "скоро". */
const SOON_RATIO = 0.1;

/** Остаток + флаги по интервалу. */
const buildAxis = (remaining: number, interval: number): MaintenanceAxisForecast => {
    const overdue = remaining < 0;
    const soon = !overdue && remaining < interval * SOON_RATIO;
    return {remaining, overdue, soon, ratio: remaining / interval};
};

/** Число, пригодное для расчёта (не null/undefined/NaN). 0 — валидно. */
const num = (v: number | null | undefined): number | null =>
    v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v);

/** Положительное число (интервал/база должны быть > 0, иначе ось не считаем). */
const positive = (v: number | null | undefined): number | null => {
    const n = num(v);
    return n !== null && n > 0 ? n : null;
};

export const selectNextMaintenanceForecast = createSelector(
    [selectCurrentMachinery, selectLastMaintenanceHistory],
    (machinery, maintenances): NextMaintenanceForecast | null => {
        if (!machinery) return null;

        // Последнее ЗАВЕРШЁННОЕ ТО — максимум по result_date.
        const lastDone = (maintenances ?? [])
            .filter((t) => t.status_id === 3)
            .reduce<ITask | null>((acc, t) => {
                if (!acc) return t;
                return (t.result_date ?? 0) > (acc.result_date ?? 0) ? t : acc;
            }, null);

        if (!lastDone) return null;

        const forecast: NextMaintenanceForecast = {hasAny: false};

        // ─── Пробег (км) ───────────────────────────────────────────
        const baseKm = positive(lastDone.result_odometer);
        const intervalKm = positive(machinery.maintenance_interval_km);
        const currentKm = num(machinery.odometer);
        if (baseKm !== null && intervalKm !== null && currentKm !== null) {
            forecast.km = buildAxis(baseKm + intervalKm - currentKm, intervalKm);
            forecast.hasAny = true;
        }

        // ─── Наработка (моточасы) ───────────────────────────────────
        const baseHours = positive(lastDone.result_operating);
        const intervalHours = positive(machinery.maintenance_interval_hours);
        const currentHours = num(machinery.operating);
        if (baseHours !== null && intervalHours !== null && currentHours !== null) {
            forecast.hours = buildAxis(baseHours + intervalHours - currentHours, intervalHours);
            forecast.hasAny = true;
        }

        // ─── Срок (дни) ─────────────────────────────────────────────
        const baseDate = positive(lastDone.result_date); // unix-мс
        const intervalMonths = positive(machinery.maintenance_interval_months);
        if (baseDate !== null && intervalMonths !== null) {
            const intervalDays = intervalMonths * 30; // ~30 дней/мес
            const dueMs = baseDate + intervalDays * DAY_MS;
            const remaining = Math.round((dueMs - Date.now()) / DAY_MS);
            forecast.days = buildAxis(remaining, intervalDays);
            forecast.hasAny = true;
        }

        return forecast.hasAny ? forecast : null;
    }
);

export const selectLightList = createSelector(
    [selectMachineryState],
    (s) => [...s.light_list].sort((a, b) => {
        const brandCmp = (a.brand ?? "").localeCompare(b.brand ?? "", "ru");
        if (brandCmp !== 0) return brandCmp;
        return (a.model ?? "").localeCompare(b.model ?? "", "ru");
    }),
);

/**
 * Строки таблицы после ОБОИХ фильтров — по типу и по группе.
 *
 * Фильтры независимы и складываются: «Экскаваторы» + «Участок 1» = машины,
 * попадающие под оба условия. Полный (нефильтрованный) список остаётся
 * доступен через selectLightList — по нему считается видимость колонок.
 */
export const selectFilteredLightList = createSelector(
    [selectLightList, selectMachineryTypeFilter, selectMachineryGroupFilter],
    (rows, typeFilter, groupFilter) => {
        const byType =
            typeFilter == null ? rows : rows.filter((m) => m.type_id === typeFilter);
        if (groupFilter == null) return byType;
        if (groupFilter === NO_GROUP_FILTER) {
            return byType.filter((m) => !m.group_id);
        }
        return byType.filter((m) => m.group_id === groupFilter);
    },
);

export const selectMachineryClientId = createSelector(
    [selectMachineryState],
    (machineryState) => machineryState.clientId
);